import {
  STRATEGY,
  calculateStops,
  decimal,
  formatNative,
  percentOf,
  skewOffsets,
  spot,
  type GateSkew,
} from "@auto-pools/core";
import { PriceScale } from "./PriceScale";
import { formatStopFromCurrent } from "../lib/stopPolicy";
import { formatMoney, formatSol } from "../lib/format";

/** The alt-season thresholds as whole percents (0.6 → 60), straight from the engine's constants. */
const ALT_BREADTH_PERCENT = String(Math.round(spot.ALT_MIN_BREADTH * 100));
const ALT_MED30_PERCENT = String(Math.round(spot.ALT_MIN_MED30 * 100));

/** A placeholder entry price for the static illustrations only; every rule number is from core. */
const ILLUSTRATION_ENTRY_PRICE = "100";

function illustration(width: string, skew: GateSkew) {
  const { below, above } = skewOffsets(width, skew);
  const lowerPrice = percentOf(
    ILLUSTRATION_ENTRY_PRICE,
    decimal("100").add(decimal(below)).toString(),
  );
  const upperPrice = percentOf(
    ILLUSTRATION_ENTRY_PRICE,
    decimal("100").add(decimal(above)).toString(),
  );
  const stops = calculateStops(lowerPrice, upperPrice, {
    enabled: true,
    lowerThresholdPercent: STRATEGY.exits.stopDistancePercent,
    upperThresholdPercent: STRATEGY.exits.stopDistancePercent,
  });
  const fromEntry = (price: string) =>
    decimal(price)
      .sub(decimal(ILLUSTRATION_ENTRY_PRICE))
      .mul(decimal("100"))
      .div(decimal(ILLUSTRATION_ENTRY_PRICE))
      .toString();
  return {
    below,
    above,
    lowerPrice,
    upperPrice,
    lowerStopLabel: stops.lowerTrigger ? formatStopFromCurrent(fromEntry(stops.lowerTrigger)) : "—",
    upperStopLabel: stops.upperTrigger ? formatStopFromCurrent(fromEntry(stops.upperTrigger)) : "—",
    lowerStop: stops.lowerTrigger,
    upperStop: stops.upperTrigger,
  };
}

const RANGES = [
  { title: "Normal width · ±10%", ...illustration(STRATEGY.gate.normalWidthPercent, "centered") },
  {
    title: "Elevated width · ±12.5%",
    ...illustration(STRATEGY.gate.elevatedWidthPercent, "centered"),
  },
  {
    title: `Skewed width · trend up`,
    ...illustration(STRATEGY.gate.normalWidthPercent, "up"),
  },
];

function RangeIllustration({ range }: { range: (typeof RANGES)[number] }) {
  return (
    <div className="autopilot-range-illustration">
      <h4>{range.title}</h4>
      <PriceScale
        compact
        min={range.lowerPrice}
        max={range.upperPrice}
        current={ILLUSTRATION_ENTRY_PRICE}
        entry={ILLUSTRATION_ENTRY_PRICE}
        lowerStop={range.lowerStop}
        upperStop={range.upperStop}
        unit="illustrative"
      />
      <p className="num">
        Range {range.below}% to +{range.above}% · stops {range.lowerStopLabel} and{" "}
        {range.upperStopLabel}
      </p>
    </div>
  );
}

/**
 * Read-only rules tab (design 47, section 8): every number below is imported from
 * `packages/core/src/autopilot/strategy.ts`, so this page cannot drift from the engine.
 */
export function AutopilotStrategy() {
  const p = STRATEGY.pool,
    g = STRATEGY.gate,
    s = STRATEGY.sizing,
    e = STRATEGY.exits,
    r = STRATEGY.regime;
  return (
    <section className="panel autopilot-strategy" aria-label="Autopilot strategy">
      <ol className="autopilot-strategy-cycle">
        <li>Rank USDC pools by LP fees (net of protocol fee) / TVL</li>
        <li>Check the entry filter on the top-ranked pool only</li>
        <li>
          Hold up to {STRATEGY.maxPositions} positions, each sized from whatever capital remains
          after the one before it
        </li>
        <li>
          Exit on the stop, the profit stop, 2 h out of range, or{" "}
          {String(e.lowYieldExitAfterSeconds / 86_400)} days earning under{" "}
          {e.lowYieldExitPercentPerDay}%/day in fees, then the result goes back to work
        </li>
      </ol>

      <h3>Pick a pool</h3>
      <table className="autopilot-strategy-table">
        <tbody>
          <tr>
            <th>TVL</th>
            <td className="num">≥ {formatMoney(p.minTvlUsd)}</td>
          </tr>
          <tr>
            <th>TVL, stock tokens ({p.issuerStocks.issuers.map((i) => i.name).join(", ")})</th>
            <td className="num">≥ {formatMoney(p.issuerStocks.minTvlUsd)}</td>
          </tr>
          <tr>
            <th>Stock tokens</th>
            <td>One position per stock · no leveraged, inverse or pre-IPO funds</td>
          </tr>
          <tr>
            <th>Pool age</th>
            <td className="num">≥ {String(p.minPoolAgeSeconds / 3600)} h</td>
          </tr>
          <tr>
            <th>Effective LPs</th>
            <td className="num">≥ {p.minEffectiveLps}</td>
          </tr>
          <tr>
            <th>Quote token</th>
            <td>USDC only · SOL/USDC is never funded from a USDC capital</td>
          </tr>
          <tr>
            <th>Token permissions</th>
            <td>Both mints supported, transfer not blocked</td>
          </tr>
          <tr>
            <th>Price freshness</th>
            <td className="num">≤ {String(p.maxPriceAgeSeconds / 60)} min</td>
          </tr>
          <tr>
            <th>Entry swap fee</th>
            <td className="num">≤ {p.maxEntryFeePercent}%</td>
          </tr>
          <tr>
            <th>Full-range-only pools</th>
            <td>Excluded</td>
          </tr>
          <tr>
            <th>Order</th>
            <td>LP fees (net of protocol fee) / TVL, highest first</td>
          </tr>
          <tr>
            <th>When it looks</th>
            <td>While flat, and 30 min after a filter refusal — no periodic 10-min scan</td>
          </tr>
          <tr>
            <th>How many pools</th>
            <td className="num">
              Up to {STRATEGY.maxPositions} at once; what is left after funding one goes to the next
              ranked pool in the same scan
            </td>
          </tr>
        </tbody>
      </table>

      <h3>Entry filter</h3>
      <table className="autopilot-strategy-table">
        <tbody>
          <tr>
            <th>Volatility ratio</th>
            <td className="num">
              ≥ {g.volSkipRatio} skip · ≥ {g.volElevatedRatio} widen
            </td>
          </tr>
          <tr>
            <th>Distance from mean</th>
            <td className="num">
              ≥ {g.distanceSkip} skip · ≥ {g.distanceCaution} widen
            </td>
          </tr>
          <tr>
            <th>Trend</th>
            <td className="num">
              ≥ {g.trendSkip} skip · {g.skewSharePercent}:{100 - Number(g.skewSharePercent)} skew at
              ≥ {g.trendSkew}
            </td>
          </tr>
          <tr>
            <th>Minute history</th>
            <td className="num">≥ {String((g.shortWindowMinutes / 60) * 2)} h (2 blocks)</td>
          </tr>
          <tr>
            <th>After a stop, this pool</th>
            <td className="num">
              {String(g.reentryMinSeconds / 60)} min, and calm (trend &lt; {g.reentryTrend},
              distance &lt; {g.reentryDistance}, vol ratio &lt; {g.volSkipRatio})
            </td>
          </tr>
        </tbody>
      </table>

      <h3>Range and stops</h3>
      <div className="autopilot-range-illustrations">
        {RANGES.map((range) => (
          <RangeIllustration key={range.title} range={range} />
        ))}
      </div>

      <h3>Exit</h3>
      <table className="autopilot-strategy-table">
        <tbody>
          <tr>
            <th>Out of range</th>
            <td>
              Above the range for {String(e.outOfRangeCloseSeconds / 3600)} h (the funding side) →
              close to USDC
            </td>
          </tr>
          <tr>
            <th>Low fee yield</th>
            <td>
              After {String(e.lowYieldExitAfterSeconds / 86_400)} days, under{" "}
              {e.lowYieldExitPercentPerDay}%/day in fees → close to USDC
            </td>
          </tr>
          <tr>
            <th>Stop</th>
            <td className="num">
              {e.stopDistancePercent}% below the lower bound or {e.stopDistancePercent}% above the
              upper bound
            </td>
          </tr>
          <tr>
            <th>Profit stop</th>
            <td>
              At +{e.profitStopThresholdPercent}% net, the lower stop moves to the range edge and
              never loosens
            </td>
          </tr>
          <tr>
            <th>After a stop close</th>
            <td>Wait {String(g.reentryMinSeconds / 60)} min, then re-rank</td>
          </tr>
        </tbody>
      </table>

      <h3>Sizing</h3>
      <table className="autopilot-strategy-table">
        <tbody>
          <tr>
            <th>Amount</th>
            <td>All free USDC, never more than {s.maxLiquiditySharePercent}% of pool liquidity</td>
          </tr>
          <tr>
            <th>Minimum entry</th>
            <td className="num">{formatMoney(s.minEntryUsd)}</td>
          </tr>
          <tr>
            <th>SOL kept for fees</th>
            <td className="num">{formatSol(formatNative(BigInt(s.minSolReserveLamports), 9))}</td>
          </tr>
          <tr>
            <th>Entry swap fee</th>
            <td className="num">Skipped above {p.maxEntryFeePercent}%</td>
          </tr>
          <tr>
            <th>Reinvestment</th>
            <td>Profit is reinvested; each entry is sized from the current capital</td>
          </tr>
          <tr>
            <th>Bad-week halving (C5)</th>
            <td>
              Every new entry is halved once the trailing 7-day return falls {r.weekLossPercent}% or
              more below zero; back to full size once it is 0% or better
            </td>
          </tr>
        </tbody>
      </table>
      <p className="autopilot-strategy-note">
        None of this is editable. The operator sets the starting capital and the two limits —
        nothing else.
      </p>

      <section className="autopilot-strategy-spot" aria-labelledby="autopilot-strategy-spot">
        <h3 id="autopilot-strategy-spot">Spot · two halves</h3>
        <p className="autopilot-strategy-note">decided on the daily close · 00:00 UTC</p>
        <ol className="autopilot-strategy-spot-rules">
          <li>
            BTC&apos;s daily close against its 100-day average decides whether there are coins at
            all. Below it, everything is sold into USDT the same night.
          </li>
          <li>
            One half holds the <b>five least volatile over 90 days</b>, chosen from the thirty most
            traded coins of the exchange, in equal parts.
          </li>
          <li>
            The other half holds the <b>five strongest over 14 days</b> while alt season is on;
            while it is off it holds BTC, and USDT when BTC is below its average.
          </li>
          <li>
            Alt season needs three things at once: at least {ALT_BREADTH_PERCENT} % of the top
            thirty above their 50-day average, median 30-day growth of the top thirty at least{" "}
            {ALT_MED30_PERCENT} %, and BTC above its 100-day average.
          </li>
          <li>
            Each half is cut into seven slices with their own weekday. Only the weekly re-pick is
            staggered — selling into USDT, coming back and the season switch happen to all seven on
            the same day.
          </li>
        </ol>
        <table className="autopilot-strategy-table">
          <tbody>
            <tr>
              <th>Universe</th>
              <td>
                The thirty most traded Bybit coins by 30-day USDT turnover — without stablecoins,
                tokenized stocks, wrappers or coins younger than 120 days
              </td>
            </tr>
            <tr>
              <th>Rebalance</th>
              <td>Once a month only, the two halves are brought back to 50/50</td>
            </tr>
            <tr>
              <th>Key</th>
              <td>
                Self-generated, only SPOT → Trade ticked, on its own subaccount — a key that can
                move money out (transfer, withdrawal, Earn, fiat, card) is refused, never stored
              </td>
            </tr>
          </tbody>
        </table>
        <p className="autopilot-strategy-note">
          History, not a promise: +39…+41 % over 15 months, with drawdowns of 23–45 % along the way.
        </p>
      </section>
    </section>
  );
}
